import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { AuthService, ErrorHandlingService, TokenService } from '../core';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class MsalAuthService {
  public isLoading = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight$: Observable<boolean> | null = null;
  private _tokenService = inject(TokenService);
  private _toastr = inject(ToastrService);
  private _router = inject(Router);
  errorHandlingService = inject(ErrorHandlingService);

  constructor(
    private msalService: MsalService,
    private authService: AuthService,
  ) {}

  login() {
    const redirectRequest = {
      scopes: ['openid', 'profile', 'email', 'offline_access', 'User.Read', 'User.ReadBasic.All', 'Directory.Read.All'],
    };
    this.msalService.loginRedirect(redirectRequest);
  }

  logout() {
    this.clearRefreshTimer();
    return this.msalService.instance.logoutRedirect({
      postLogoutRedirectUri: '/login',
    });
  }

  handleRedirectObservable() {
    this.isLoading = true;
    return this.msalService.instance
      .handleRedirectPromise()
      .then((result: AuthenticationResult | null) => {
        if (result) {
          this.msalService.instance.setActiveAccount(result.account);
          this.loginWithMicrosoft(result);
        } else {
          this.login();
        }
      })
      .catch((error) => {
        this.errorHandlingService.handleError(error, 'Error while redirect');
        throw error;
      })
      .finally(() => (this.isLoading = false));
  }

  loginWithMicrosoft(result: AuthenticationResult) {
    this.isLoading = true;
    const account = this.getActiveAccount();
    return this.authService.loginWithMicrosoft(result, account!.localAccountId).subscribe(
      (loginResponse) => {
        if (loginResponse.token) {
          this._tokenService.setToken(loginResponse.token);
          this._tokenService.setTokenEntra(result.idToken);
          this._toastr.success('Login successful! Welcome back!', 'Success');
          this.scheduleTokenRefresh();
          const previousUrl = localStorage.getItem('previousUrl');
          const redirectUrl = localStorage.getItem('redirectUrl') || '/dashboard';
          localStorage.removeItem('redirectUrl');
          if (redirectUrl !== previousUrl) {
            localStorage.setItem('previousUrl', redirectUrl);
            this._router.navigateByUrl(redirectUrl);
          } else {
            localStorage.removeItem('previousUrl');
            this._router.navigateByUrl('/dashboard');
          }
        } else if (loginResponse.message) {
          const toastRef = this._toastr.info(loginResponse.message, '', {
            timeOut: 100000,
            closeButton: false,
          });

          toastRef.onHidden.subscribe(() => {
            this.logout();
          });
        }
      },
      (error) => {
        this.errorHandlingService.handleError(error, 'Error when try to Login');
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  isLoggedIn(): boolean {
    return this.msalService.instance.getActiveAccount() != null;
  }

  getTokenSilent(): Observable<AuthenticationResult | null> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return of(null);

    const silentRequest: SilentRequest = {
      scopes: ['openid', 'profile', 'email', 'offline_access', 'User.Read', 'User.ReadBasic.All', 'Directory.Read.All'],
      account: account,
    };
    return this.msalService.acquireTokenSilent(silentRequest);
  }

  refreshTokensSilently(): Observable<boolean> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.getTokenSilent().pipe(
      switchMap((result) => {
        if (!result) return of(false);

        const account = this.getActiveAccount();
        if (!account) return of(false);

        return this.authService.loginWithMicrosoft(result, account.localAccountId).pipe(
          tap((loginResponse) => {
            if (loginResponse.token) {
              this._tokenService.setToken(loginResponse.token);
              this._tokenService.setTokenEntra(result.idToken);
              this.scheduleTokenRefresh();
            }
          }),
          map((loginResponse) => !!loginResponse.token),
        );
      }),
      catchError(() => of(false)),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.refreshInFlight$;
  }

  getActiveAccount() {
    return this.msalService.instance.getActiveAccount();
  }

  private scheduleTokenRefresh() {
    this.clearRefreshTimer();

    const jwtPayload = this._tokenService.getJwtPayload();
    const exp = jwtPayload?.exp;
    if (!exp) return;

    const refreshAt = exp * 1000 - 5 * 60 * 1000;
    const delay = Math.max(0, refreshAt - Date.now());

    this.refreshTimer = setTimeout(() => {
      if (!this.isLoggedIn()) return;
      this.refreshTokensSilently().subscribe();
    }, delay);
  }

  private clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
